import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, Printer, X } from 'lucide-react';
import { api, apiBlob, post } from './api';
import { useAuth } from './auth';
import { can } from './permissions';
import { printerRecentlyOnline } from './machine-view';
import { rememberPrinter } from './printer-preference';
import { defaultMachinePrinter, machinePrintAction, machinePrintPending, machinePrintUnresolved } from './machine-print-flow';
import { unavailablePrinterMessage } from './printing-view';
import { PrintJobRecovery } from './print-job-recovery';
import { connectionStationText } from './connection-station';
import { PrintConnectionStatus } from './print-connection-status';
import { connectionHealth, printerChoiceStatus } from './print-connection-view';
import { usePrinterMonitor } from './use-printer-monitor';
import './machine-print.css';

const ended = (state: string) => !['QUEUED', 'LEASED', 'RECEIVED', 'SENDING', 'UNKNOWN'].includes(state);

export function MachineQrPrintDialog({ machine, count, onClose }: { machine: { id: string; code: string; name: string } | null; count: number; onClose: () => void }) {
  const { user } = useAuth();
  const [pdf, setPdf] = useState(''), [error, setError] = useState(''), [deviceError, setDeviceError] = useState('');
  const [preview, setPreview] = useState<{id:string;code:string;name:string;departmentName:string;qrImage:string}[]>([]);
  const [mode, setMode] = useState<'external' | 'esp32'>(machine ? 'esp32' : 'external');
  const [devices, setDevices] = useState<any>(null), [printerId, setPrinterId] = useState('');
  const [printJob, setPrintJob] = useState<any>(null), [busy, setBusy] = useState(false), [historyLoading, setHistoryLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [now, setNow] = useState(Date.now());
  const sending = useRef(false), printKey = useRef(crypto.randomUUID());
  const selectedByUser = useRef(false);
  const closeRef = useRef<HTMLButtonElement>(null), dialogRef = useRef<HTMLDivElement>(null);
  const canPrint = !!machine && can(user, 'printing.create') && can(user, 'printing.view');
  const printers = devices?.printers ?? [];
  const printer = printers.find((p: any) => p.id === printerId);
  const ready = !!printer && printerRecentlyOnline(printer, now);
  const previousPrinter = printers.find((p: any) => p.id === printJob?.printerId);
  const action = machinePrintAction(printJob, printerId, { reprint: can(user, 'printing.reprint'), cancel: can(user, 'printing.cancel') });
  const blocked = machinePrintUnresolved(printJob?.state);
  const activeMode = canPrint ? mode : 'external';
  const monitorError = usePrinterMonitor(printer ? [printer.id] : [], activeMode === 'esp32', user?.id);

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], select:not(:disabled), iframe') ?? [])
        .filter(el => el.getClientRects().length > 0);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); before?.focus(); };
  }, [onClose]);

  useEffect(() => {
    let disposed = false, url = '';
    const query = machine ? `?ids=${encodeURIComponent(machine.id)}` : '';
    Promise.all([apiBlob(`/machines/qr-labels.pdf${query}`), api(`/machines/qr-labels${query}`)]).then(([blob, labels]) => {
      if (disposed) return;
      url = URL.createObjectURL(blob); setPdf(url); setPreview(labels);
    }).catch(e => { if (!disposed) setError(e.message); });
    return () => { disposed = true; if (url) URL.revokeObjectURL(url); };
  }, [machine?.id]);

  useEffect(() => {
    if (!canPrint) { setDevices(null); return; }
    let disposed = false;
    const load = () => api('/printing/printers').then(data => {
      if (!disposed) { setDevices(data); setDeviceError(''); }
    }).catch(e => { if (!disposed) { setDevices(null); setDeviceError(e.message); } });
    void load();
    const timer = setInterval(load, 5000), clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { disposed = true; clearInterval(timer); clearInterval(clock); };
  }, [canPrint, user?.id]);

  useEffect(() => {
    if (busy || blocked) return;
    if (!selectedByUser.current || !printers.some((p: any) => p.id === printerId))
      setPrinterId(defaultMachinePrinter(printers, user?.id, preview[0]?.departmentName, now));
  }, [devices, now, printerId, user?.id, busy, blocked, preview]);
  useEffect(() => { printKey.current = crypto.randomUUID(); }, [machine?.id, printerId, devices?.policy?.version, printJob?.id]);
  useEffect(() => {
    if (!canPrint || !machine) return;
    let disposed = false; setHistoryLoading(true);
    void api(`/printing/source-job?sourceId=${encodeURIComponent(machine.id)}&labelType=MACHINE`).then(job => {
      if (!disposed && job) setPrintJob((old: any) => old ?? job);
    }).catch(e => { if (!disposed) setDeviceError(e.message); }).finally(() => { if (!disposed) setHistoryLoading(false); });
    return () => { disposed = true; };
  }, [machine?.id, canPrint, user?.id]);

  useEffect(() => {
    const job = printJob;
    if (!job?.id || ended(job.state)) return;
    let disposed = false, inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await api(`/printing/jobs/${job.id}`);
        if (!disposed) {
          setPrintJob((current: any) => current?.id === job.id ? { ...current, ...result } : current);
          setDeviceError('');
        }
      } catch (e: any) { if (!disposed) setDeviceError(e.message); }
      finally { inFlight = false; }
    };
    void poll(); const timer = setInterval(poll, 2000);
    return () => { disposed = true; clearInterval(timer); };
  }, [printJob?.id, printJob?.state]);
  const print = async () => {
    if (sending.current || !canPrint || !ready || !machine || historyLoading || !action.allowed) return;
    if (printJob && !confirm(action.replaceQueued ? `Hủy yêu cầu cũ chưa bắt đầu gửi và in mã ${machine.code} tại ${printer.name} · ${printer.location_name}?` :
      `Bạn đã kiểm tra giấy và muốn in thêm mã ${machine.code} tại ${printer.name} · ${printer.location_name}? Nếu nhãn cũ đã ra, đây sẽ là một bản in thêm.`)) return;
    sending.current = true; setBusy(true); setDeviceError(''); setNotice('');
    try {
      const job = printJob ? await post(`/printing/jobs/${printJob.id}/reprint`, { printerId, requestKey: printKey.current,
        reason: action.replaceQueued ? 'Người dùng chuyển máy in; hủy yêu cầu cũ trước khi gửi nhãn' : 'Người dùng đã kiểm tra giấy và chọn in lại tại máy in đã chọn',
        ...(action.replaceQueued ? { replaceQueued: true } : {}) }) :
        await post('/printing/jobs', { sourceId: machine.id, labelType: 'MACHINE', printerId, requestKey: printKey.current });
      const actualId = job.printerId ?? printerId;
      rememberPrinter(actualId, user?.id);
      setPrintJob({ printerId, printerCaption: `${printer.name} · ${printer.location_name}`, ...job });
      if (!job.existing) { selectedByUser.current = true; setPrinterId(actualId); setNotice(`Đã gửi yêu cầu in tới ${job.printerCaption || `${printer.name} · ${printer.location_name}`}.`); }
      else setNotice('Nhãn đang có yêu cầu khác. App mở yêu cầu đó, chưa gửi thêm nhãn.');
    } catch (e: any) { setDeviceError(e.message); }
    finally { sending.current = false; setBusy(false); }
  };

  return <div className="machine-print-overlay" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={dialogRef} className="machine-print-dialog" role="dialog" aria-modal="true" aria-labelledby="machine-print-title">
      <header><div><h2 id="machine-print-title">{machine ? `In mã QR ${machine.code}` : 'In tất cả mã QR máy'}</h2>
        <p>{machine ? machine.name : `${count} máy trong phạm vi được cấp quyền. PDF A4, 6 nhãn mỗi trang.`}</p></div>
        <button ref={closeRef} type="button" className="icon-btn" aria-label="Đóng hộp thoại in mã QR" onClick={onClose}><X /></button></header>
      <div className="machine-print-modes" aria-label="Cách in mã QR">
        {canPrint && <button className={activeMode === 'esp32' ? 'primary' : 'secondary'} aria-pressed={activeMode === 'esp32'} onClick={() => setMode('esp32')}>Máy in nhãn</button>}
        <button className={activeMode === 'external' ? 'primary' : 'secondary'} aria-pressed={activeMode === 'external'} onClick={() => setMode('external')}>Máy in ngoài / PDF</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {activeMode === 'external' && <section><p>Tải PDF hoặc mở để chọn máy in. Dùng giấy A4 và tỉ lệ 100%.</p>
        {pdf ? <><div className="machine-print-buttons">
          <a className="primary" href={pdf} download={machine ? `QR-${machine.code.replace(/[^a-z0-9-]/gi, '_')}.pdf` : 'ma-qr-tat-ca-may.pdf'}><Download /> Tải PDF</a>
          <a className="secondary" href={pdf} target="_blank" rel="noopener noreferrer"><ExternalLink /> Mở PDF để in máy ngoài</a>
        </div><div className="machine-label-preview" aria-label="Xem trước nội dung nhãn QR">
          {preview.map(item => <article key={item.id}><b>{item.code}</b><span>{item.name}</span><small>{item.departmentName || 'Chưa phân phòng/xưởng'}</small><img src={item.qrImage} alt={`Mã QR tiếp quản máy ${item.code}`} /></article>)}
        </div></> : !error && <p role="status">Đang tạo PDF...</p>}
      </section>}
      {activeMode === 'esp32' && <section>
        <p>Chọn máy in cho lần in này. Kết quả lần trước được hiển thị riêng bên dưới.</p>
        <label>Máy in nhãn<select aria-label="Máy in nhãn" value={printerId} onChange={e => { selectedByUser.current = true; setPrinterId(e.target.value); setNotice(''); rememberPrinter(e.target.value, user?.id); }} disabled={busy || blocked}>
          <option value="">Chọn máy in</option>{printers.map((p: any) => <option key={p.id} value={p.id} disabled={!printerRecentlyOnline(p, now)}>{p.name} · {connectionStationText(p.location_name)} · {printerChoiceStatus(p)}</option>)}</select></label>
        {!blocked && <div className="machine-print-buttons"><button className="primary" onClick={print} disabled={busy || historyLoading || !canPrint || !ready || !action.allowed} aria-busy={busy}><Printer /> {busy ? 'Đang gửi…' : historyLoading ? 'Đang kiểm tra yêu cầu cũ…' : action.label}</button></div>}
        {notice && <p className="machine-print-notice" role="status">{notice}</p>}
        {printJob && machinePrintPending(printJob.state) && <p className="machine-print-hint">Yêu cầu cũ ở {connectionStationText(printJob.printerCaption)} chưa bắt đầu gửi. Chọn máy in khác rồi bấm nút phía trên để hủy yêu cầu cũ và chuyển sang máy mới.</p>}
        {printJob && machinePrintPending(printJob.state) && (!can(user, 'printing.cancel') || !can(user, 'printing.reprint')) && <p>Bạn cần quyền hủy và in lại để chuyển yêu cầu đang chờ. Nhờ người quản lý hỗ trợ.</p>}
        {blocked && <p className="machine-print-hint">Yêu cầu cũ đang gửi hoặc chưa rõ nhãn đã ra chưa. Cần kiểm tra và xử lý yêu cầu bên dưới trước khi in thêm hoặc đổi máy.</p>}
        {!devices && !deviceError && <p role="status">Đang tìm máy in đã lưu…</p>}
        {monitorError && <p className="machine-print-hint">Chưa bật được theo dõi nhanh: {monitorError} Thao tác in vẫn tự kết nối.</p>}
        {printer && <PrintConnectionStatus health={connectionHealth(printer)} unavailable={!!deviceError} />}
        {devices && !printers.length && !devices.unavailablePrinters?.length && <p className="machine-print-hint">{devices.policy?.requireArea !== false && !devices.context ? 'Bạn chưa được phân công khu vực hoặc chưa nhận máy. Liên hệ người điều phối.' : 'Chưa có máy in trong khu vực bạn được sử dụng. Liên hệ người quản lý để thiết lập.'}</p>}
        {(devices?.unavailablePrinters ?? []).map((p: any) => <p key={p.id} className="machine-print-hint">{unavailablePrinterMessage(p)} <Link to="/printing">Mở thiết lập máy in</Link></p>)}
        {printJob && <section className="machine-print-previous" aria-label="Yêu cầu in đã lưu"><h3>Yêu cầu in đã lưu</h3><PrintJobRecovery job={printJob} onChanged={setPrintJob} health={previousPrinter?.deviceHealth} unavailable={!!deviceError} allowReprint={false} /></section>}
        <details><summary>Trợ giúp khi không in được</summary><p>Bật máy in, kiểm tra giấy và đặt gần Trạm kết nối.</p><Link to="/printing">Máy in & lịch sử in</Link>{printJob && <small>Mã hỗ trợ: {printJob.id}</small>}</details>
      </section>}
      {deviceError && <p className="error" role="alert">{deviceError}</p>}
      <p className="machine-print-hint">In nhãn không đổi trạng thái máy hoặc tạo lại mã QR. Chỉ tải/in những máy bạn có quyền xem.</p>
    </div>
  </div>;
}
